<template>
  <div class="to-page" :class="{ 'to-page--mobile': isMobile }" dir="rtl">
    <div class="to-card">
      <!-- Header -->
      <header class="to-head">
        <span class="to-head__icon"><v-icon size="20" color="primary">mdi-translate</v-icon></span>
        <div class="to-head__titles">
          <div class="to-head__title">
            {{ id ? $t('translationOrderEdit') : $t('translationOrder') }}
            <span v-if="number" class="to-chip to-num">#{{ number }}</span>
          </div>
          <div class="to-head__sub">
            <template v-if="customer">{{ customer.name || '' }}</template>
            <template v-if="items.length"> · {{ items.length }} {{ $t('trOrderItemsCount') }}</template>
          </div>
        </div>
        <template v-if="!formType">
          <button type="button" class="to-tile" :title="$t('translationList')" @click="goTo('translationList')">
            <v-icon size="20">mdi-format-list-bulleted</v-icon>
          </button>
          <button type="button" class="to-tile" :title="$t('back')" @click="$router.go(-1)">
            <v-icon size="20">mdi-arrow-left</v-icon>
          </button>
        </template>
      </header>

      <!-- Steps -->
      <nav class="to-steps">
        <button
          v-for="(st, i) in stepDefs"
          :key="st.key"
          type="button"
          class="to-step"
          :class="{ 'to-step--active': e1 == i + 1, 'to-step--done': e1 > i + 1 }"
          @click="e1 = i + 1"
        >
          <span class="to-step__dot">
            <v-icon v-if="e1 > i + 1" size="15" color="#fff">mdi-check</v-icon>
            <template v-else>{{ i + 1 }}</template>
          </span>
          <span class="to-step__label">{{ $t(st.key) }}</span>
          <v-icon size="16" class="to-step__icon">{{ st.icon }}</v-icon>
        </button>
      </nav>
      <v-progress-linear :active="!render || isLoading || loading" indeterminate color="primary" height="2" />

      <v-form v-if="render" ref="form" v-model="valid" lazy-validation class="to-body">
        <!-- STEP 1 -->
        <div v-show="e1 == 1" class="to-stack">
          <section class="to-sec">
            <div class="to-sec__head"><v-icon size="17">mdi-account-box-outline</v-icon>{{ $t('trOrderSecInfo') }}</div>
            <div class="to-grid to-grid--4">
              <div class="to-span-2">
                <autocomplete v-model="customer" title="customer" type="person" :new-btn="true" :value="customer" />
              </div>
              <v-text-field
                v-model="number" dense outlined hide-details="auto" clearable type="number"
                :rules="[v => !!v || $t('is-required')]" :label="$t('number')" @blur="getTranslatorNumber"
              />
              <autocomplete v-model="languageGroup" title="languageGroup" type="translationLanguage" :new-btn="true" :value="languageGroup" />
              <div>
                <v-text-field
                  id="from" v-model="date" :label="$t('date')" :rules="[v => !!v || $t('is-required')]"
                  outlined dense clearable hide-details="auto" append-icon="mdi-calendar-blank-outline"
                />
                <date-picker
                  v-model="date" element="from" :locale="$store.state.date_locale" :format="$store.state.date_format"
                  auto-submit :min="$store.state.startDate" :max="$store.state.endDate" :color="primaryColor" @close="setDate"
                />
              </div>
              <div>
                <v-text-field
                  id="deliveryDateId" v-model="deliveryDate" :label="$t('deliveryDate')"
                  outlined dense clearable hide-details append-icon="mdi-calendar-clock-outline" @keyup="deliveryDate = null"
                />
                <date-picker
                  v-model="deliveryDate" element="deliveryDateId" :locale="$store.state.date_locale" :format="$store.state.date_format"
                  auto-submit :min="deliveryDate" :color="primaryColor" @close="setDate"
                />
              </div>
              <div class="to-span-2">
                <autocomplete v-model="translator" title="translator" type="person" :new-btn="true" :data="JSON.stringify({type: 'translator'})" :value="translator" />
              </div>
              <div class="to-span-2">
                <status v-model="status" :hideconfig="true" type="translation" :value="status" />
              </div>
              <div class="to-span-2">
                <tag v-model="tag" :solo="false" :input="tag" :height="25" />
              </div>
            </div>
          </section>

          <section class="to-sec">
            <div class="to-sec__head"><v-icon size="17">mdi-file-cabinet</v-icon>{{ $t('trOrderSecNonTranslate') }}</div>
            <div class="to-addrow">
              <autocomplete v-model="documentOwner" title="documentOwner" type="person" :new-btn="true" :value="documentOwner" />
              <aicomplete v-model="nontranslateDocument" title="non-translateDocuments" type="nontranslateDocument" :new-btn="true" :val="nontranslateDocument" />
              <description v-model="nontranslateDocumentContent" :outlined="true" type="sell" :value="nontranslateDocumentContent" />
              <v-btn color="primary" depressed class="to-addrow__btn" @click="addAttachment">
                <v-icon small class="me-1">mdi-plus</v-icon>{{ $t('add') }}
              </v-btn>
            </div>
            <table class="to-table">
              <thead>
                <tr><th>{{ $t('row') }}</th><th>{{ $t('name') }}</th><th>{{ $t('content') }}</th><th /></tr>
              </thead>
              <tbody>
                <tr v-for="(item, key) in nontranslateDocumentItems" :key="key">
                  <td class="to-num">{{ key + 1 }}</td>
                  <td>{{ item.nontranslateDocument ? item.nontranslateDocument.name : '' }}</td>
                  <td>{{ item.content }}</td>
                  <td class="to-table__act">
                    <button type="button" class="to-icon-btn to-icon-btn--danger" :title="$t('remove')" @click="removeAttachment(key)">
                      <v-icon size="18">mdi-trash-can-outline</v-icon>
                    </button>
                  </td>
                </tr>
                <tr v-if="!nontranslateDocumentItems || !nontranslateDocumentItems.length">
                  <td colspan="4" class="to-empty">{{ $t('trOrderNoRows') }}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>

        <!-- STEP 2 -->
        <div v-show="e1 == 2" class="to-split">
          <v-form ref="formStep2" v-model="validStep2" lazy-validation class="to-stack">
            <section class="to-sec">
              <div class="to-sec__head">
                <v-icon size="17">mdi-file-document-outline</v-icon>{{ $t('trOrderSecDoc') }}
                <span v-if="editStatus" class="to-chip to-chip--warn">{{ $t('edit') }}</span>
              </div>
              <div class="to-grid to-grid--3">
                <aicomplete v-model="documentType" title="documentType" type="setConfig" :new-btn="true" :data="JSON.stringify({key:'t_docuemtnType', type:'translation'})" :val="documentType" />
                <aicomplete v-model="fromLanguage" title="fromLanguage" type="setConfig" :new-btn="true" :data="JSON.stringify({key:'t_languages', type:'translation'})" :val="fromLanguage" />
                <aicomplete v-model="toLanguage" title="toLanguage" type="setConfig" :new-btn="true" :data="JSON.stringify({key:'t_languages', type:'translation'})" :val="toLanguage" />
                <v-text-field v-model="documentCount" dense outlined clearable hide-details="auto" type="number" :rules="[v => !!v || $t('is-required')]" :label="$t('documentCount')" />
                <div class="to-span-2">
                  <v-text-field v-model="customerTitle" dense outlined clearable hide-details="auto" :rules="[v => !!v || $t('is-required')]" :label="$t('customerTitle')" />
                </div>
                <div class="to-span-3">
                  <description v-model="customertContent" :outlined="true" type="sell" :value="customertContent" />
                </div>
              </div>
            </section>

            <section class="to-sec">
              <div class="to-sec__head"><v-icon size="17">mdi-counter</v-icon>{{ $t('Number_version_urgency') }}</div>
              <div class="to-grid to-grid--5">
                <v-text-field v-model="baseCount" dense outlined clearable hide-details="auto" min="0" type="number" :label="$t('baseCount')" :rules="exampleRules" @focus="focusEvent" />
                <v-text-field v-model="addDocumentCount" dense outlined clearable hide-details type="number" :label="$t('addDocumentCount')" @focus="focusEvent" />
                <v-text-field v-model="percentDocumentCount" dense outlined clearable hide-details="auto" type="number" suffix="%" :label="$t('percentDocumentCount')" :rules="exampleRules" @focus="focusEvent" />
                <v-text-field v-model="countSpecialTranslat" dense outlined clearable hide-details type="number" :label="$t('countSpecialTranslat')" @focus="focusEvent" />
                <v-text-field v-model="countSpecialTranslatAdditional" dense outlined clearable hide-details type="number" :label="$t('countSpecialTranslatAdditional')" @focus="focusEvent" />
              </div>
            </section>

            <section class="to-sec">
              <div class="to-sec__head"><v-icon size="17">mdi-stamper</v-icon>{{ $t('typeTranslation&approval') }}</div>
              <div
                class="to-unofficial"
                :class="{ 'to-svc--on': unofficial, 'to-svc--off': unofficialDisabled }"
                @click="!unofficialDisabled && (unofficial = unofficial ? null : 'active')"
              >
                <v-icon size="18">mdi-file-hidden</v-icon>
                <span>{{ $t('unofficial') }}</span>
                <v-switch v-model="unofficial" value="active" :disabled="unofficialDisabled" inset dense hide-details class="to-switch" @click.native.stop />
              </div>
              <div class="to-svc-grid">
                <div
                  v-for="s in serviceDefs"
                  :key="s.key"
                  class="to-svc"
                  :class="{ 'to-svc--on': $data[s.key], 'to-svc--off': s.disabled }"
                >
                  <div class="to-svc__top" @click="toggleService(s)">
                    <v-icon size="18" class="to-svc__icon">{{ s.icon }}</v-icon>
                    <span class="to-svc__name">{{ $t(s.key) }}</span>
                    <v-switch v-model="$data[s.key]" value="active" :disabled="s.disabled" inset dense hide-details class="to-switch" @click.native.stop />
                  </div>
                  <div class="to-svc__row">
                    <input
                      v-model="$data[s.key + 'Count']" type="number" min="0" class="to-svc__count to-num"
                      :disabled="!$data[s.key]" :aria-label="$t('trOrderCount')"
                    >
                    <span class="to-svc__x">×</span>
                    <span class="to-svc__unit to-num" :title="$t('trOrderUnit')">{{ $numberFormat(+$data[s.key + 'Price'] || 0) }}</span>
                    <span class="to-svc__eq">=</span>
                    <b class="to-svc__line to-num">{{ $numberFormat(serviceLine(s.key)) }}</b>
                  </div>
                </div>
              </div>
              <div v-show="embassy" class="to-grid to-grid--3 mt-4">
                <v-text-field v-model="embassyName" :label="$t('embassyName')" dense outlined hide-details />
                <v-text-field v-model="embassyCost" :label="$t('embassyCost')" dense outlined clearable hide-details />
              </div>
            </section>

            <section class="to-sec">
              <div class="to-sec__head">
                <v-icon size="17">mdi-format-list-checks</v-icon>{{ $t('translationItems') }}
                <span class="to-chip">{{ items.length }}</span>
              </div>
              <order-item v-if="items.length" :items="items" :edit-index="editStatus ? editIndex : null" @edit="editItem" @remove="removeItem" />
              <div v-else class="to-empty to-empty--box">
                <v-icon size="28">mdi-text-box-plus-outline</v-icon>
                {{ $t('trOrderNoItems') }}
              </div>
            </section>
          </v-form>

          <!-- Item summary aside -->
          <aside class="to-aside">
            <section class="to-sec to-sec--aside">
              <div class="to-sec__head"><v-icon size="17">mdi-cash</v-icon>{{ $t('trOrderSecFees') }}</div>
              <div class="to-fee-grid">
                <money v-model="basePrice" :label="$t('baseTranslate')" :dense="true" :outlined="true" :solo="false" :hide-details="true" value-when-is-empty="" :options="options" class="to-money" />
                <money v-model="addDocumentPrice" :label="$t('addDocument')" :disabled="true" :dense="true" :outlined="true" :solo="false" :hide-details="true" value-when-is-empty="" :options="options" class="to-money" />
                <money v-model="specialTranslatPrice" :label="$t('specialTranslat')" :dense="true" :outlined="true" :solo="false" :hide-details="true" value-when-is-empty="" :options="options" class="to-money" />
                <money v-model="specialTranslatAddPrice" :label="$t('specialTranslatAdditional')" :disabled="true" :dense="true" :outlined="true" :solo="false" :hide-details="true" value-when-is-empty="" :options="options" class="to-money" />
                <money v-model="urgentAmount" :label="$t('urgentAmount')" :clearable="true" :dense="true" :outlined="true" :solo="false" :hide-details="true" value-when-is-empty="" :options="options" class="to-money" />
                <money v-model="discountTranslate" :label="$t('discountTranslate')" :clearable="true" :dense="true" :outlined="true" :solo="false" :hide-details="true" value-when-is-empty="" :options="options" class="to-money" />
                <money v-model="discountService" :label="$t('discountService')" :clearable="true" :dense="true" :outlined="true" :solo="false" :hide-details="true" value-when-is-empty="" :options="options" class="to-money to-span-2" />
              </div>
            </section>
            <section class="to-sec to-sec--aside">
              <div class="to-sec__head"><v-icon size="17">mdi-receipt-text-outline</v-icon>{{ $t('trOrderSecItemSummary') }}</div>
              <dl class="to-sum">
                <div><dt>{{ $t('baseTranslate') }}</dt><dd class="to-num">{{ $numberFormat(basePriceTotal || 0) }}</dd></div>
                <div><dt>{{ $t('addDocument') }}</dt><dd class="to-num">{{ $numberFormat(addDocumentPriceTotal || 0) }}</dd></div>
                <div><dt>{{ $t('specialTranslat') }}</dt><dd class="to-num">{{ $numberFormat(specialTranslatTotal || 0) }}</dd></div>
                <div><dt>{{ $t('specialTranslatAdditional') }}</dt><dd class="to-num">{{ $numberFormat(specialTranslatAddPriceTotal || 0) }}</dd></div>
                <div><dt>{{ $t('totalStamp&Approval') }}</dt><dd class="to-num">{{ $numberFormat(totalStamp || 0) }}</dd></div>
                <div><dt>{{ $t('urgentAmount') }}</dt><dd class="to-num">{{ $numberFormat(urgentAmount || 0) }}</dd></div>
                <div class="to-sum__neg"><dt>{{ $t('discountTranslate') }} + {{ $t('discountService') }}</dt><dd class="to-num">{{ $numberFormat((+discountTranslate || 0) + (+discountService || 0)) }}</dd></div>
                <div class="to-sum__grand"><dt>{{ $t('totalCost') }}</dt><dd class="to-num">{{ $numberFormat(totalCost || 0) }}</dd></div>
              </dl>
              <div class="to-aside__actions">
                <v-btn :color="editStatus ? 'primary' : 'success'" depressed block class="app-form-action-btn" @click="addItem">
                  <v-icon small class="me-1">{{ editStatus ? 'mdi-content-save-edit-outline' : 'mdi-playlist-plus' }}</v-icon>
                  {{ editStatus ? $t('edit') : $t('addToOrder') }}
                </v-btn>
                <v-btn v-if="editStatus" color="grey" outlined block class="app-form-action-btn mt-2" @click="clear">
                  {{ $t('trOrderCancelEdit') }}
                </v-btn>
              </div>
            </section>
          </aside>
        </div>

        <!-- STEP 3 -->
        <div v-show="e1 == 3" class="to-split">
          <v-form ref="formStep3" v-model="validStep3" lazy-validation class="to-stack">
            <section class="to-sec">
              <div class="to-sec__head">
                <v-icon size="17">mdi-cash-multiple</v-icon>{{ $t('trOrderSecPayments') }}
                <span class="to-spacer" />
                <v-btn color="success" outlined small class="to-sm-btn" @click="openComponent('payment', customer, 'recieve')">
                  <v-icon small class="me-1">mdi-cash-plus</v-icon>{{ $t('cashReceive') }}
                </v-btn>
                <v-btn color="purple" outlined small class="to-sm-btn" @click="openComponent('check', customer, 'recieveCheck')">
                  <v-icon small class="me-1">mdi-checkbook</v-icon>{{ $t('recieveCheck') }}
                </v-btn>
              </div>
              <div class="to-sub">{{ $t('cashReceiveList') }}</div>
              <table class="to-table">
                <thead>
                  <tr><th /><th>{{ $t('payer') }}</th><th>{{ $t('amount') }}</th><th>{{ $t('date') }}</th><th>{{ $t('bank') }}</th><th>{{ $t('currency') }}</th><th /></tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in recieves" :key="'r' + i">
                    <td class="to-num">{{ +i + 1 }}</td>
                    <td>{{ item.account ? item.account.name : '' }}</td>
                    <td class="to-num">{{ $numberFormat(item.amount) }}</td>
                    <td class="to-num">{{ item.date }}</td>
                    <td>{{ item.bank ? item.bank.name : '' }}</td>
                    <td>{{ item.currency ? item.currency.name : '' }}</td>
                    <td class="to-table__act">
                      <button type="button" class="to-icon-btn to-icon-btn--danger" :disabled="loading" :title="$t('remove')" @click="removeCash(i)">
                        <v-icon size="18">mdi-trash-can-outline</v-icon>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!recieves || !recieves.length"><td colspan="7" class="to-empty">{{ $t('trOrderNoRows') }}</td></tr>
                </tbody>
              </table>
              <div class="to-sub mt-5">{{ $t('recieveCheckList') }}</div>
              <table class="to-table">
                <thead>
                  <tr><th /><th>{{ $t('payer') }}</th><th>{{ $t('amount') }}</th><th>{{ $t('dueDate') }}</th><th>{{ $t('date') + ' ' + $t('issue') }}</th><th>{{ $t('bank') }}</th><th>{{ $t('currency') }}</th><th /></tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in checks" :key="'c' + i">
                    <td class="to-num">{{ +i + 1 }}</td>
                    <td>{{ item.account ? item.account.name : '' }}</td>
                    <td class="to-num">{{ $numberFormat(item.amount) }}</td>
                    <td class="to-num">{{ item.dueDate }}</td>
                    <td class="to-num">{{ item.date }}</td>
                    <td>{{ item.bankName }}</td>
                    <td>{{ item.currency ? item.currency.name : '' }}</td>
                    <td class="to-table__act">
                      <button type="button" class="to-icon-btn to-icon-btn--danger" :disabled="loading" :title="$t('remove')" @click="removeCheck(i)">
                        <v-icon size="18">mdi-trash-can-outline</v-icon>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!checks || !checks.length"><td colspan="8" class="to-empty">{{ $t('trOrderNoRows') }}</td></tr>
                </tbody>
              </table>
            </section>
          </v-form>

          <aside class="to-aside">
            <section class="to-sec to-sec--aside">
              <div class="to-sec__head"><v-icon size="17">mdi-receipt-text-check-outline</v-icon>{{ $t('trOrderSecInvoice') }}</div>
              <dl class="to-sum">
                <div><dt>{{ $t('totalTranslation') }}</dt><dd class="to-num">{{ $numberFormat(+totals.basePriceTotal + +totals.addDocumentPriceTotal + +totals.specialTranslatTotal + +totals.specialTranslatAddPriceTotal) }}</dd></div>
                <div><dt>{{ $t('urgentAmount') }}</dt><dd class="to-num">{{ $numberFormat(totals.urgentAmount) }}</dd></div>
                <div v-for="s in serviceDefs" :key="'t' + s.key"><dt>{{ $t(s.key) }}</dt><dd class="to-num">{{ $numberFormat(totals[s.key + 'Price'] || 0) }}</dd></div>
                <div class="to-sum__neg"><dt>{{ $t('discountTranslate') }}</dt><dd class="to-num">{{ $numberFormat(totals.discountTranslate) }}</dd></div>
                <div class="to-sum__neg"><dt>{{ $t('discountService') }}</dt><dd class="to-num">{{ $numberFormat(totals.discountService) }}</dd></div>
                <div class="to-sum__grand"><dt>{{ $t('totalCost') }}</dt><dd class="to-num">{{ $numberFormat(totals.totalCost) }}</dd></div>
                <div><dt>{{ $t('sumPayment') }}</dt><dd class="to-num">{{ $numberFormat(totalPayment) }}</dd></div>
                <div class="to-sum__remain"><dt>{{ $t('remain') }}</dt><dd class="to-num">{{ $numberFormat(+totals.totalCost - +totalPayment) }}</dd></div>
              </dl>
            </section>
          </aside>
        </div>
      </v-form>

      <!-- Footer -->
      <footer v-if="render" class="to-foot">
        <v-btn
          v-if="e1 > 2"
          color="primary" depressed class="app-form-action-btn" :loading="isLoading"
          @click="id ? update() : create()"
        >
          <v-icon small class="me-1">mdi-check</v-icon>{{ $t('submit') }}
        </v-btn>
        <v-btn v-else color="primary" depressed class="app-form-action-btn" @click="e1++">
          {{ $t('continue') }}<v-icon small class="ms-1">mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn v-if="e1 > 1" color="grey" outlined class="app-form-action-btn" @click="e1--">
          <v-icon small class="me-1">mdi-chevron-right</v-icon>{{ $t('previous') }}
        </v-btn>
        <div class="to-foot__total">
          <span>{{ $t('totalCost') }}</span>
          <b class="to-num">{{ $numberFormat(totals.totalCost || 0) }}</b>
          <template v-if="totalPayment">
            <span class="to-foot__sep" />
            <span>{{ $t('remain') }}</span>
            <b class="to-num">{{ $numberFormat(+totals.totalCost - +totalPayment) }}</b>
          </template>
        </div>
        <v-btn color="grey" outlined class="app-form-action-btn" @click="goTo('translationList')">
          {{ $t('cancel') }}
        </v-btn>
      </footer>
    </div>

    <v-snackbar v-model="snakbar" :color="(snakbarColor ? snakbarColor : '')" top style="z-index: 1000000000000 !important;">
      {{ snakbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = !snakbar">{{ $t('close') }}</v-btn>
      </template>
    </v-snackbar>
    <dialog-component :dialog-prop="innerComponent" :data-prop="dialogData" @get="setRecieve" @close="innerComponent = false" />
  </div>
</template>

<script>
  import aicomplete from '../../components/general/AIcomplete/index'
  import autocomplete from '../../components/general/patial/autocomplete'
  import General from '../../../../Mixins/general'
  import Description from '../../components/general/description/Description'
  import OrderItem from './partial/orderItem'
  export default {
    name: 'TranslateOrder',
    components: {
      aicomplete,
      Description,
      autocomplete,
      OrderItem,
      dialogComponent: () => import('../../components/general/dialog/index'),
      Status: () => import('../../components/general/Status/status'),
      Tag: () => import('../../components/general/Tag/tag'),
    },
    mixins: [General],
    props: ['formType'],
    data: () => ({
      options: {
        locale: 'en-US',
        prefix: '',
        suffix: ' ',
        precision: 0,
      },
      exampleRules: [
        v => !!v || 'This field is required',
        v => (v && v >= 0) || 'The number must be bigger than zero',
      ],
      snakbar: false,
      render: false,
      isLoading: false,
      dialogData: null,
      alert: false,
      e1: 1,
      translator: null,
      embassyName: null,
      discountService: 0,
      discountTranslate: 0,
      unofficial: null,
      embassy: null,
      embassyCost: null,
      embassyCount: 0,
      embassyPrice: 0,
      equalOriginal: null,
      equalOriginalCount: 0,
      equalOriginalPrice: 0,
      inquiry: null,
      inquiryCount: 0,
      inquiryPrice: 0,
      verificationServices: null,
      verificationServicesCount: 0,
      verificationServicesPrice: 0,
      officeServices: null,
      officeServicesCount: 0,
      officeServicesPrice: 0,
      stampTranslator: null,
      stampTranslatorCount: 0,
      stampTranslatorPrice: 0,
      judicialApproval: null,
      judicialApprovalCount: 0,
      judicialApprovalPrice: 0,
      foreignApproval: null,
      foreignApprovalCount: 0,
      foreignApprovalPrice: 0,
      specialTranslatPrice: 0,
      otherPrice: 0,
      date: null,
      countSpecialTranslatAdditional: 0,
      percentDocumentCount: 50,
      countSpecialTranslat: 0,
      urgentAmount: 0,
      addDocumentCount: 0,
      baseCount: 0,
      basePrice: 0,
      customerTitle: null,
      customertContent: null,
      documentCount: null,
      toLanguage: null,
      fromLanguage: null,
      documentType: null,
      documentOwner: null,
      deliveryDate: null,
      languageGroup: null,
      from: null,
      tag: null,
      nontranslateDocument: null,
      nontranslateDocumentContent: null,
      nontranslateDocumentItems: [],
      number: 1,
      content: null,
      valid: null,
      validStep2: null,
      validStep3: null,
      message: null,
      alertType: null,
      customer: null,
      type: null,
      id: null,
      items: [],
      status: null,
      editIndex: null,
      editStatus: false,
      group: null,
      recieves: [],
      checks: [],
      sum: null,
      priceChange: true,
      loading: false,
      loadingEqualOriginal: false,
    }),
    computed: {
      stepDefs () {
        return [
          { key: 'customer', icon: 'mdi-account-outline' },
          { key: 'translateDocuments', icon: 'mdi-file-document-multiple-outline' },
          { key: 'receiveCash', icon: 'mdi-cash-check' },
        ]
      },
      unofficialDisabled () {
        return !!(this.judicialApproval || this.foreignApproval || this.stampTranslator)
      },
      serviceDefs () {
        var off = !!this.unofficial
        return [
          { key: 'judicialApproval', icon: 'mdi-scale-balance', disabled: off },
          { key: 'foreignApproval', icon: 'mdi-earth', disabled: off },
          { key: 'stampTranslator', icon: 'mdi-stamper', disabled: off },
          { key: 'officeServices', icon: 'mdi-briefcase-outline', disabled: false },
          { key: 'verificationServices', icon: 'mdi-shield-check-outline', disabled: off },
          { key: 'equalOriginal', icon: 'mdi-content-copy', disabled: off },
          { key: 'inquiry', icon: 'mdi-magnify-scan', disabled: off },
          { key: 'embassy', icon: 'mdi-bank-outline', disabled: off },
        ]
      },
      primaryColor () {
        return this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light'].primary
      },
      approvalRules () {
        return [
          v => (v && v >= 0) || 'The number must be bigger than zero',
        ]
      },
      totalStamp () {
        var total = 0
        var foreignApprovalPrice = this.foreignApprovalPrice * this.foreignApprovalCount
        var embassyPrice = this.embassyPrice * this.embassyCount
        var verificationServicesPrice = this.verificationServicesPrice * this.verificationServicesCount
        var equalOriginalPrice = this.equalOriginalPrice * this.equalOriginalCount
        var inquiryPrice = (this.inquiryPrice || 0) * (this.inquiryCount || 0)
        var officeServicesPrice = this.officeServicesPrice * this.officeServicesCount
        var judicialApprovalPrice = this.judicialApprovalPrice * this.judicialApprovalCount
        var stampTranslatorPrice = this.stampTranslatorPrice * this.stampTranslatorCount
        total = foreignApprovalPrice + embassyPrice + verificationServicesPrice + equalOriginalPrice + inquiryPrice + stampTranslatorPrice + judicialApprovalPrice + officeServicesPrice
        return total
      },
      totalCost () {
        var total = 0
        total = parseFloat(this.basePriceTotal) + parseFloat(this.addDocumentPriceTotal) +
          parseFloat(this.specialTranslatTotal) +
          parseFloat(this.specialTranslatAddPriceTotal) + parseFloat(this.totalStamp) + parseFloat(this.urgentAmount) - parseFloat(this.discountTranslate) - parseFloat(this.discountService)
        return total
      },
      basePriceTotal () {
        var total = 0
        if (this.basePrice && this.baseCount) {
          total = this.basePrice * this.baseCount
        }
        return total
      },
      addDocumentPrice () {
        var price = 0
        if (this.basePrice && this.addDocumentCount && this.percentDocumentCount) {
          price = (parseFloat(this.percentDocumentCount) / 100) * parseFloat(this.basePrice)
        }
        return price
      },
      addDocumentPriceTotal () {
        var total = 0
        if (this.addDocumentPrice && this.addDocumentCount) {
          total = this.addDocumentPrice * this.addDocumentCount
        }
        return total
      },
      specialTranslatTotal () {
        var total = 0
        if (this.specialTranslatPrice && this.countSpecialTranslat) {
          total = this.specialTranslatPrice * this.countSpecialTranslat
        }
        return total
      },
      specialTranslatAddPrice () {
        var price = 0
        if (this.basePrice && this.specialTranslatPrice && this.percentDocumentCount) {
          price = (parseFloat(this.percentDocumentCount) / 100) * parseFloat(this.specialTranslatPrice)
        }
        return price
      },
      specialTranslatAddPriceTotal () {
        var price = 0
        if (this.basePrice && this.specialTranslatAddPrice && this.countSpecialTranslatAdditional) {
          price = this.specialTranslatAddPrice * this.countSpecialTranslatAdditional
        }
        return price
      },
      totalPayment () {
        var cash = 0
        var check = 0
        this.recieves.map(function (value, index) {
          cash = +cash + +value.amount
        })
        this.checks.map(function (value, index) {
          check = +check + +value.amount
        })
        return +cash + +check
      },
      totals: function () {
        var basePriceTotal = 0
        var addDocumentPriceTotal = 0
        var specialTranslatTotal = 0
        var specialTranslatAddPriceTotal = 0
        var urgentAmount = 0
        var stampTranslatorPrice = 0
        var judicialApprovalPrice = 0
        var officeServicesPrice = 0
        var equalOriginalPrice = 0
        var inquiryPrice = 0
        var foreignApprovalPrice = 0
        var verificationServicesPrice = 0
        var embassyPrice = 0
        var discountService = 0
        var discountTranslate = 0
        var totalCost = 0
        this.items.map((value, d) => {
          basePriceTotal += parseFloat(value.basePriceTotal)
          addDocumentPriceTotal += parseFloat(value.addDocumentPriceTotal)
          specialTranslatTotal += parseFloat(value.specialTranslatTotal)
          specialTranslatAddPriceTotal += parseFloat(value.specialTranslatAddPriceTotal)
          urgentAmount += parseFloat(value.urgentAmount)
          stampTranslatorPrice += parseFloat(value.stampTranslatorPrice * value.stampTranslatorCount)
          judicialApprovalPrice += parseFloat(value.judicialApprovalPrice * value.judicialApprovalCount)
          officeServicesPrice += parseFloat(value.officeServicesPrice * value.officeServicesCount)
          equalOriginalPrice += parseFloat(value.equalOriginalPrice * value.equalOriginalCount)
          inquiryPrice += parseFloat((value.inquiryPrice || 0) * (value.inquiryCount || 0))
          foreignApprovalPrice += parseFloat(value.foreignApprovalPrice * value.foreignApprovalCount)
          verificationServicesPrice += parseFloat(value.verificationServicesPrice * value.verificationServicesCount)
          embassyPrice += parseFloat(value.embassyPrice * value.embassyCount)
          discountService += parseFloat(value.discountService)
          discountTranslate += parseFloat(value.discountTranslate)
          totalCost += parseFloat(value.totalCost)
        })
        var total = {
          basePriceTotal: basePriceTotal,
          addDocumentPriceTotal: addDocumentPriceTotal,
          specialTranslatTotal: specialTranslatTotal,
          specialTranslatAddPriceTotal: specialTranslatAddPriceTotal,
          urgentAmount: urgentAmount,
          stampTranslatorPrice: stampTranslatorPrice,
          officeServicesPrice: officeServicesPrice,
          judicialApprovalPrice: judicialApprovalPrice,
          equalOriginalPrice: equalOriginalPrice,
          inquiryPrice: inquiryPrice,
          foreignApprovalPrice: foreignApprovalPrice,
          embassyPrice: embassyPrice,
          verificationServicesPrice: verificationServicesPrice,
          discountService: discountService,
          discountTranslate: discountTranslate,
          totalCost: totalCost,
        }
        return total
      },
    },
    watch: {
      unofficial (val, oldVal) {
        if (val) {
          this.officeServices = 'active'
          // this.officeServicesCount = parseFloat(this.baseCount) + parseFloat(this.addDocumentCount)
        }
      },
      judicialApproval (val, oldVal) {
        if (val) {
          this.judicialApprovalCount = parseFloat(this.baseCount) + parseFloat(this.addDocumentCount)
        } else {
          this.judicialApprovalCount = 0
        }
      },
      foreignApproval (val, oldVal) {
        if (val) {
          this.foreignApprovalCount = parseFloat(this.baseCount) + parseFloat(this.addDocumentCount)
        } else {
          this.foreignApprovalCount = 0
        }
      },
      stampTranslator (val, oldVal) {
        if (val) {
          this.stampTranslatorCount = parseFloat(this.baseCount) + parseFloat(this.addDocumentCount)
        } else {
          this.stampTranslatorCount = 0
        }
      },
      officeServices (val, oldVal) {
        if (val) {
          this.officeServicesCount = parseFloat(this.baseCount) + parseFloat(this.addDocumentCount)
        } else {
          this.officeServicesCount = 0
        }
      },
      verificationServices (val, oldVal) {
        if (val) {
          this.verificationServicesCount = parseFloat(this.baseCount) + parseFloat(this.addDocumentCount)
        } else {
          this.verificationServicesCount = 0
        }
      },
      equalOriginal (val, oldVal) {
        if (this.skipCountReset) return
        if (val) {
          this.equalOriginalCount = parseFloat(this.baseCount) + parseFloat(this.addDocumentCount)
        } else {
          this.equalOriginalCount = 0
        }
      },
      inquiry (val, oldVal) {
        if (this.skipCountReset) return
        if (val) {
          this.inquiryCount = parseFloat(this.baseCount) + parseFloat(this.addDocumentCount)
        } else {
          this.inquiryCount = 0
        }
      },
      embassy (val, oldVal) {
        if (val) {
          this.embassyCount = parseFloat(this.baseCount) + parseFloat(this.addDocumentCount)
        } else {
          this.embassyCount = 0
        }
      },
      snakbar () {
      },
      toLanguage (val, oldVal) {
        if (val && this.fromLanguage && this.documentType && this.priceChange) {
          this.getPrice()
        }
        this.priceChange = true
      },
    },
    created () {
      // non-reactive, kept out of $data (payload)
      this.skipCountReset = false
    },
    beforeMount () {
      if (this.$route.params.id != null) {
        this.show()
        this.id = this.$route.params.id
        this.render = false
      } else {
        this.render = true
        this.getTranslatorNumber()
      }
    },
    mounted () {
    },
    methods: {
      toggleService (s) {
        if (s.disabled) return
        this[s.key] = this[s.key] ? null : 'active'
      },
      serviceLine (key) {
        if (!this[key]) return 0
        return (parseFloat(this[key + 'Price']) || 0) * (parseFloat(this[key + 'Count']) || 0)
      },
      getTranslatorNumber () {
        this.loading = true
        var app = this
        app.isLoading = true
        clearTimeout(this.timer)
        this.timer = setTimeout(function () {
          app.$axios.get(app.$endpoint + 'getdetail', {
            params: {
              type: app.type,
              id: app.id,
              number: app.number,
              select: 'getTranslatorNumber',
            },
          })
            .then(res => {
              res = res.data
              if (res.status === 'error') {
                if (app.checkNumber) {
                  app.snakbarText = res.message
                  app.snakbarColor = 'pink'
                  app.snakbar = true
                }
                app.number = res.number
              }
              app.checkNumber = true
            })
            .catch(err => {
              app.serverErrorHandeler(err)
            })
            .finally(() => (app.isLoading = false))
        }, 50)
        this.loading = false
      },
      setRecieve (data) {
        if (data.type === 'recieve') {
          this.recieves.push(data)
        }
        if (data.type === 'recieveCheck') {
          this.checks.push(data)
        }
      },
      removeCash (index) {
        var item = this.recieves[index]
        var app = this
        if (item.id) {
          app.loading = true
          app.$axios.delete(this.$endpoint + 'payments/' + item.id)
            .then(res => {
              res = res.data
              app.recieves.splice(index, 1)
            })
            .finally(() => (app.loading = false))
        } else {
          app.recieves.splice(index, 1)
        }
      },
      removeCheck (index) {
        var item = this.checks[index]
        var app = this
        if (item.id) {
          app.loading = true
          app.$axios.delete(this.$endpoint + 'checks/' + item.id)
            .then(res => {
              res = res.data
              app.checks.splice(index, 1)
            })
            .finally(() => (app.loading = false))
        } else {
          app.checks.splice(index, 1)
        }
      },
      openComponent (component, account, type) {
        this.dialogData = {
          name: component,
          accountProp: account,
          notStore: true,
          type: type,
        }
        this.innerComponent = true
      },
      addItem () {
        if (!this.$refs.formStep2.validate()) {
          this.snakbarText = this.$t('trOrderFillRequired')
          this.snakbarColor = 'pink'
          this.snakbar = true
          this.$nextTick(() => {
            var el = this.$el.querySelector('.to-split .error--text')
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          })
          return false
        }
        var item = {
          documentType: this.documentType,
          fromLanguage: this.fromLanguage,
          toLanguage: this.toLanguage,
          customerTitle: this.customerTitle,
          customertContent: this.customertContent,
          documentCount: this.documentCount,
          baseCount: this.baseCount,
          basePrice: this.basePrice,
          addDocumentCount: this.addDocumentCount,
          percentDocumentCount: this.percentDocumentCount,
          countSpecialTranslat: this.countSpecialTranslat,
          countSpecialTranslatAdditional: this.countSpecialTranslatAdditional,
          unofficial: this.unofficial,
          judicialApproval: this.judicialApproval,
          judicialApprovalCount: this.judicialApprovalCount,
          judicialApprovalPrice: this.judicialApprovalPrice,
          foreignApproval: this.foreignApproval,
          foreignApprovalCount: this.foreignApprovalCount,
          foreignApprovalPrice: this.foreignApprovalPrice,
          stampTranslator: this.stampTranslator,
          stampTranslatorCount: this.stampTranslatorCount,
          stampTranslatorPrice: this.stampTranslatorPrice,
          officeServices: this.officeServices,
          officeServicesCount: this.officeServicesCount,
          officeServicesPrice: this.officeServicesPrice,
          verificationServices: this.verificationServices,
          verificationServicesCount: this.verificationServicesCount,
          verificationServicesPrice: this.verificationServicesPrice,
          equalOriginal: this.equalOriginal,
          equalOriginalCount: this.equalOriginalCount,
          equalOriginalPrice: this.equalOriginalPrice,
          inquiry: this.inquiry,
          inquiryCount: this.inquiryCount,
          inquiryPrice: this.inquiryPrice,
          embassy: this.embassy,
          embassyCount: this.embassyCount,
          embassyPrice: this.embassyPrice,
          embassyName: this.embassyName,
          embassyCost: this.embassyCost,
          urgentAmount: this.urgentAmount,
          discountService: this.discountService,
          discountTranslate: this.discountTranslate,
          basePriceTotal: this.basePriceTotal,
          addDocumentPriceTotal: this.addDocumentPriceTotal,
          specialTranslatTotal: this.specialTranslatTotal,
          specialTranslatAddPriceTotal: this.specialTranslatAddPriceTotal,
          totalCost: this.totalCost,
        }
        if (this.editStatus) {
          this.removeItem(this.editIndex)
        }
        this.items.push(item)
        // this.$refs.formStep2.reset()
        this.clear()
        this.$refs.formStep2.resetValidation()
      },
      getPrice () {
        var app = this
        app.loading = true
        app.clearing = true
        app.showing = true
        app.$axios.get(this.$endpoint + 'translation/show', {
          params: {
            getPrice: 'yes',
            type: (app.documentType ? app.documentType.id : null),
            fromLang: (app.fromLanguage ? app.fromLanguage.id : null),
            toLang: (app.toLanguage ? app.toLanguage.id : null),
            userCategory: (app.toLanguage ? app.toLanguage.id : null),
          },
        })
          .then(res => {
            res = res.data
            app.basePrice = res.fee
            app.equalOriginalPrice = res.equal_original
            app.inquiryPrice = res.inquiry || 0
            app.verificationServicesPrice = res.verification_services
            app.officeServicesPrice = res.office_services
            app.stampTranslatorPrice = res.stamp_translator
            app.judicialApprovalPrice = res.judicial_approval
            app.foreignApprovalPrice = res.foreign_approval
            app.embassyPrice = res.embassy
            app.specialTranslatPrice = res.special_translation
            app.otherPrice = res.other
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      addAttachment () {
        this.nontranslateDocumentItems.push({
          nontranslateDocument: this.nontranslateDocument,
          documentOwner: this.documentOwner,
          content: this.nontranslateDocumentContent,
        })
        this.nontranslateDocument = null
        this.nontranslateDocumentContent = null
      },
      removeAttachment (key) {
        this.nontranslateDocumentItems.splice(key, 1)
      },
      create () {
        var app = this
        app.isLoading = true
        app.sum = app.totals
        app.$axios.post(this.$endpoint + 'translation', app.$data)
          .then(res => {
            app.goTo('translationList')
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => (this.isLoading = false))
      },
      show () {
        var app = this
        app.loading = true
        app.clearing = true
        app.showing = true
        app.$axios.get(this.$endpoint + 'translation/' + this.$route.params.id + '/edit', {
          params: {
            id: this.$route.params.id,
          },
        })
          .then(res => {
            res = res.data
            app.$nextTick(() => {
              app.id = res.id
              app.customer = res.customer
              app.translator = res.translator
              app.status = (res.status ? res.status.status : null)
              app.tag = res.tags
              app.number = res.number
              app.date = res.date
              app.checks = res.check
              app.recieves = res.payment
              app.deliveryDate = res.delivery_date
              app.nontranslateDocumentItems = res.nontranslateDocumentItems
              app.items = res.article
              app.render = true
            })
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      update () {
        var app = this
        app.isLoading = true
        app.sum = app.totals
        app.$axios.put(this.$endpoint + 'translation/' + this.$route.params.id, this.$data)
          .then(res => {
            res = res.data
            app.goTo('translationList')
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.isLoading = false))
      },
      removeItem (index) {
        this.items.splice(index, 1)
      },
      editItem (item, index) {
        this.priceChange = false
        this.editIndex = index
        this.editStatus = true
        this.documentType = item.documentType
        this.fromLanguage = item.fromLanguage
        this.toLanguage = item.toLanguage
        this.customerTitle = item.customerTitle
        this.customertContent = item.customertContent
        this.documentCount = item.documentCount
        this.baseCount = item.baseCount
        this.basePrice = item.basePrice
        this.addDocumentCount = item.addDocumentCount
        this.percentDocumentCount = item.percentDocumentCount
        this.countSpecialTranslat = item.countSpecialTranslat
        this.countSpecialTranslatAdditional = item.countSpecialTranslatAdditional
        this.unofficial = item.unofficial
        this.judicialApproval = item.judicialApproval
        this.judicialApprovalCount = item.judicialApprovalCount
        this.judicialApprovalPrice = item.judicialApprovalPrice
        this.foreignApproval = item.foreignApproval
        this.foreignApprovalCount = item.foreignApprovalCount
        this.foreignApprovalPrice = item.foreignApprovalPrice
        this.stampTranslator = item.stampTranslator
        this.stampTranslatorCount = item.stampTranslatorCount
        this.stampTranslatorPrice = item.stampTranslatorPrice
        this.officeServices = item.officeServices
        this.officeServicesCount = item.officeServicesCount
        this.officeServicesPrice = item.officeServicesPrice
        this.verificationServices = item.verificationServices
        this.verificationServicesCount = item.verificationServicesCount
        this.verificationServicesPrice = item.verificationServicesPrice
        this.equalOriginal = item.equalOriginal
        this.equalOriginalCount = item.equalOriginalCount
        this.equalOriginalPrice = item.equalOriginalPrice
        this.inquiry = item.inquiry || null
        this.inquiryCount = item.inquiryCount || 0
        this.inquiryPrice = item.inquiryPrice || 0
        this.embassy = item.embassy
        this.embassyCount = item.embassyCount
        this.embassyPrice = item.embassyPrice
        this.embassyName = item.embassyName
        this.embassyCost = item.embassyCost
        this.urgentAmount = item.urgentAmount
        this.discountService = item.discountService
        this.discountTranslate = item.discountTranslate
        this.skipCountReset = true
        this.$nextTick(() => { this.skipCountReset = false })
      },
      cashReceive () {

      },
      clear () {
        this.editStatus = false
        this.editIndex = null
        this.documentType = null
        this.fromLanguage = null
        this.toLanguage = null
        this.customerTitle = ''
        this.customertContent = ''
        this.documentCount = 0
        this.baseCount = 0
        this.basePrice = null
        this.addDocumentCount = 0
        this.percentDocumentCount = 50
        this.countSpecialTranslat = 0
        this.countSpecialTranslatAdditional = 0
        this.unofficial = null
        this.judicialApproval = null
        this.judicialApprovalCount = 0
        this.judicialApprovalPrice = null
        this.foreignApproval = null
        this.foreignApprovalCount = 0
        this.foreignApprovalPrice = null
        this.stampTranslator = null
        this.stampTranslatorCount = 0
        this.stampTranslatorPrice = null
        this.officeServices = null
        this.officeServicesCount = 0
        this.officeServicesPrice = null
        this.verificationServices = null
        this.verificationServicesCount = 0
        this.verificationServicesPrice = null
        this.equalOriginal = null
        this.equalOriginalCount = 0
        this.equalOriginalPrice = null
        this.inquiry = null
        this.inquiryCount = 0
        this.inquiryPrice = null
        this.embassy = null
        this.embassyCount = null
        this.embassyPrice = null
        this.embassyName = null
        this.embassyCost = null
        this.urgentAmount = 0
        this.discountService = 0
        this.discountTranslate = 0
      },
    },
  }
</script>

<style scoped>
.to-page { height: calc(100vh - 98px); box-sizing: border-box; }
.to-page ::v-deep .v-input { height: auto !important; }
.to-card {
  background: #fff; border: 1px solid rgba(15,23,42,.08); border-radius: 6px;
  box-shadow: 0 1px 3px rgba(15,23,42,.05); width: 100%; height: 100%;
  display: flex; flex-direction: column; overflow: hidden;
}
.to-head { display: flex; align-items: center; gap: 12px; padding: 12px 18px; border-bottom: 1px solid rgba(15,23,42,.07); flex-shrink: 0; }
.to-head__icon { width: 40px; height: 40px; border-radius: 6px; background: rgba(25,118,210,.1); display: grid; place-items: center; flex-shrink: 0; }
.to-head__titles { flex: 1; min-width: 0; }
.to-head__title { font-weight: 700; font-size: 1rem; display: flex; align-items: center; gap: 8px; }
.to-head__sub { opacity: .6; font-size: .78rem; margin-top: 2px; min-height: 1em; }
.to-tile { width: 40px; height: 40px; border-radius: 6px; background: rgba(0,0,0,.05); display: grid; place-items: center; flex-shrink: 0; }
.to-tile:hover { background: rgba(25,118,210,.1); }
.to-chip { font-size: .72rem; font-weight: 600; padding: 2px 8px; border-radius: 6px; background: rgba(25,118,210,.1); color: var(--v-primary-base); }
.to-chip--warn { background: rgba(245,124,0,.12); color: #e65100; }

.to-steps { display: flex; gap: 6px; padding: 8px 18px; border-bottom: 1px solid rgba(15,23,42,.07); flex-shrink: 0; }
.to-step { flex: 1; display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 6px; color: rgba(0,0,0,.6); transition: background .15s; }
.to-step:hover { background: rgba(0,0,0,.04); }
.to-step__dot { width: 26px; height: 26px; border-radius: 50%; display: grid; place-items: center; font-size: .8rem; font-weight: 700; background: rgba(0,0,0,.08); flex-shrink: 0; }
.to-step__label { font-weight: 600; font-size: .88rem; }
.to-step__icon { margin-inline-start: auto; opacity: .5; }
.to-step--active { background: rgba(25,118,210,.1); color: var(--v-primary-base); }
.to-step--active .to-step__dot { background: var(--v-primary-base); color: #fff; }
.to-step--done .to-step__dot { background: #2e7d32; }

.to-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden; padding: 16px 18px; background: rgba(0,0,0,.02); }
.to-stack { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
.to-split { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 14px; align-items: start; }
.to-aside { position: sticky; top: 0; display: flex; flex-direction: column; gap: 14px; }

.to-sec { flex-shrink: 0; background: #fff; border: 1px solid rgba(15,23,42,.08); border-radius: 6px; padding: 14px 16px 18px; }
.to-sec__head { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: .88rem; margin-bottom: 18px; color: var(--v-primary-base); }
.to-sub { font-size: .8rem; font-weight: 600; opacity: .7; margin-bottom: 8px; }
.to-spacer { flex: 1; }
.to-sm-btn { border-radius: 6px !important; }

.to-grid { display: grid; gap: 24px 14px; }
.to-grid--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.to-grid--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.to-grid--5 { grid-template-columns: repeat(5, minmax(0, 1fr)); }
.to-grid > *, .to-addrow > * { margin: 0 !important; min-width: 0; }
.to-span-2 { grid-column: span 2; }
.to-span-3 { grid-column: span 3; }
.to-addrow { display: grid; grid-template-columns: 1fr 1fr 2fr auto; gap: 14px; align-items: start; margin-bottom: 16px; }
.to-addrow__btn { height: 40px !important; border-radius: 6px; }

.to-table { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid rgba(15,23,42,.08); border-radius: 6px; overflow: hidden; font-size: .85rem; }
.to-table th { background: #f6f8fb; font-weight: 600; font-size: .78rem; color: rgba(0,0,0,.6); text-align: start; padding: 9px 12px; }
.to-table td { padding: 8px 12px; border-top: 1px solid rgba(15,23,42,.06); }
.to-table tbody tr:hover td { background: rgba(25,118,210,.03); }
.to-table__act { width: 44px; text-align: center; }
.to-icon-btn { width: 30px; height: 30px; border-radius: 6px; display: inline-grid; place-items: center; }
.to-icon-btn--danger { color: #d81b60; }
.to-icon-btn--danger:hover { background: rgba(216,27,96,.1); }
.to-empty { text-align: center; opacity: .5; padding: 14px !important; font-size: .82rem; }
.to-empty--box { display: flex; flex-direction: column; align-items: center; gap: 6px; border: 1px dashed rgba(15,23,42,.15); border-radius: 6px; padding: 24px !important; }

.to-unofficial { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border: 1px solid rgba(15,23,42,.1); border-radius: 6px; margin-bottom: 12px; cursor: pointer; font-weight: 600; font-size: .86rem; }
.to-unofficial span { flex: 1; }
.to-svc-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.to-svc { border: 1px solid rgba(15,23,42,.1); border-radius: 6px; padding: 8px 10px; display: flex; flex-direction: column; gap: 6px; transition: border-color .15s, background .15s; min-width: 0; }
.to-svc__top { display: flex; align-items: center; gap: 6px; cursor: pointer; min-height: 28px; }
.to-svc__icon { opacity: .55; }
.to-svc__name { flex: 1; font-weight: 600; font-size: .8rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.to-svc__row { display: flex; align-items: center; gap: 6px; font-size: .78rem; min-width: 0; }
.to-svc__count { width: 46px; height: 28px; flex: 0 0 auto; border: 1px solid rgba(15,23,42,.18); border-radius: 6px; text-align: center; font-size: .8rem; outline: none; background: #fff; }
.to-svc__count:focus { border-color: var(--v-primary-base); }
.to-svc__count:disabled { background: rgba(0,0,0,.04); color: rgba(0,0,0,.4); }
.to-svc__x, .to-svc__eq { opacity: .45; flex: 0 0 auto; }
.to-svc__unit { opacity: .7; white-space: nowrap; }
.to-svc__line { margin-inline-start: auto; white-space: nowrap; font-size: .82rem; }
.to-svc--on { border-color: var(--v-primary-base); background: rgba(25,118,210,.04); }
.to-svc--on .to-svc__icon { color: var(--v-primary-base); opacity: 1; }
.to-svc--on .to-svc__line { color: var(--v-primary-base); }
.to-svc--off { opacity: .5; }
.to-svc--off .to-svc__top { cursor: not-allowed; }
.theme--dark .to-svc__count { background: #2a2a2a; color: #fff; border-color: rgba(255,255,255,.2); }
.to-switch { margin: 0 !important; padding: 0 !important; flex: 0 0 auto; transform: scale(.85); transform-origin: center; }

.to-fee-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 22px 10px; }
.to-fee-grid > * { margin: 0 !important; min-width: 0; }
.to-page ::v-deep .to-money input { direction: ltr; text-align: left; }
.to-page ::v-deep fieldset { border-radius: 6px !important; }

.to-sum { margin: 0; display: flex; flex-direction: column; gap: 2px; font-size: .84rem; }
.to-sum > div { display: flex; justify-content: space-between; gap: 8px; padding: 6px 0; border-bottom: 1px dashed rgba(15,23,42,.08); }
.to-sum dt { opacity: .7; }
.to-sum dd { margin: 0; font-weight: 600; }
.to-sum__neg dd { color: #d81b60; }
.to-sum__grand { border-bottom: 0 !important; background: rgba(25,118,210,.08); border-radius: 6px; padding: 10px 12px !important; margin-top: 6px; }
.to-sum__grand dt { opacity: 1 !important; font-weight: 700; }
.to-sum__grand dd { color: var(--v-primary-base); font-size: 1rem; font-weight: 800; }
.to-sum__remain { border-bottom: 0 !important; background: rgba(46,125,50,.08); border-radius: 6px; padding: 10px 12px !important; }
.to-sum__remain dd { color: #2e7d32; font-weight: 800; }
.to-aside__actions { margin-top: 14px; }

.to-foot { display: flex; align-items: center; gap: 10px; padding: 12px 18px; background: #fafbfd; border-top: 1px solid rgba(15,23,42,.07); flex-shrink: 0; }
.to-foot ::v-deep .v-btn { border-radius: 6px; }
.to-foot__total { flex: 1; display: flex; justify-content: center; align-items: baseline; gap: 8px; font-size: .85rem; }
.to-foot__total span { opacity: .6; }
.to-foot__total b { font-size: 1.05rem; }
.to-foot__sep { width: 1px; height: 14px; background: rgba(0,0,0,.2); margin: 0 6px; align-self: center; }
.to-num { direction: ltr; unicode-bidi: isolate; }

@media (max-width: 1400px) { .to-svc-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 1150px) {
  .to-split { grid-template-columns: 1fr; }
  .to-aside { position: static; }
  .to-svc-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .to-grid--5 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
.to-page--mobile { height: auto; }
.to-page--mobile .to-card { height: auto; border: 0; border-radius: 0; overflow: visible; }
.to-page--mobile .to-body { padding: 12px; overflow: visible; }
.to-page--mobile .to-step__label, .to-page--mobile .to-step__icon { display: none; }
.to-page--mobile .to-step { justify-content: center; }
.to-page--mobile .to-step--active .to-step__label { display: inline; }
.to-page--mobile .to-grid--3, .to-page--mobile .to-grid--4, .to-page--mobile .to-grid--5,
.to-page--mobile .to-svc-grid, .to-page--mobile .to-addrow { grid-template-columns: 1fr; }
.to-page--mobile .to-span-2, .to-page--mobile .to-span-3 { grid-column: auto; }
.to-page--mobile .to-foot { position: sticky; bottom: 0; flex-wrap: wrap; }
.to-page--mobile .to-foot__total { order: -1; flex-basis: 100%; }
.to-page--mobile .to-table { display: block; overflow-x: auto; }

.theme--dark .to-card, .theme--dark .to-sec { background: #1e1e1e; border-color: rgba(255,255,255,.1); }
.theme--dark .to-head, .theme--dark .to-steps, .theme--dark .to-svc, .theme--dark .to-unofficial, .theme--dark .to-table { border-color: rgba(255,255,255,.1); }
.theme--dark .to-table th { background: #262626; color: rgba(255,255,255,.7); }
.theme--dark .to-foot { background: #252525; border-color: rgba(255,255,255,.1); }
.theme--dark .to-tile { background: rgba(255,255,255,.08); }
.theme--dark .to-step { color: rgba(255,255,255,.7); }
</style>
